import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  Paged,
  SellerProfile,
  SellerService,
} from '../../services/seller.service';
import { Product, EntityType } from '../../models/product';
import { AppService } from '../../app.service';
import { BusinessService } from '../../services/business.service';

/**
 * SCRUM-87 + SCRUM-88 — the public seller / store page.
 *
 * The screen both epics land on: store identity and VIP badge from 88, follow
 * button and counts from 87, plus the seller's live ads.
 */
@Component({
  selector: 'app-seller-profile',
  templateUrl: './seller-profile.component.html',
  styleUrls: ['./seller-profile.component.scss'],
  standalone: false,
})
export class SellerProfileComponent implements OnInit {
  profile: SellerProfile | null = null;
  ads: Product[] = [];
  filteredAds: Product[] = [];
  selectedCategory = 'all';
  selectedSubcategory = 'all';
  categoryOptions: Array<{ id: string; name: string }> = [];
  subcategoryOptions: Array<{ id: string; name: string }> = [];

  loading = true;
  loadingMore = false;
  followBusy = false;
  notFound = false;
  failed = false;

  private page = 1;
  hasNext = false;

  constructor(
    private route: ActivatedRoute,
    private sellerService: SellerService,
    private appService: AppService,
    private business: BusinessService
  ) {}

  // ── SCRUM-264: business page ──────────────────────────────────────────────

  get isBusiness(): boolean {
    return this.profile?.is_business === true;
  }

  /** SCRUM-261 — the tick means a verified license, never just VIP. */
  get isVerified(): boolean {
    return this.profile?.store?.verified === true;
  }

  get verifiedText(): string {
    return this.profile?.store?.verified_text || 'רישיון אומת';
  }

  get domains(): Array<{ category: string; name: string }> {
    return this.profile?.store?.domains || [];
  }

  get serviceAreas(): string[] {
    return this.profile?.store?.service_areas || [];
  }

  get contactPerson(): string {
    return this.profile?.store?.contact_person || '';
  }

  get storePhone(): string {
    return this.profile?.store?.phone || '';
  }

  get license() {
    return this.profile?.store?.license || null;
  }

  /** "לאתר העסק" only for a valid http(s) URL. */
  get websiteUrl(): string | null {
    const w = (this.profile?.store?.website || '').trim();
    return /^https?:\/\/[^\s.]+\.[^\s]+$/i.test(w) ? w : null;
  }

  /** SCRUM-267 — counted separately from page visits and inquiries. */
  onWebsiteClick(): void {
    this.business.track('website_click', { seller_id: this.profile?.id });
  }

  onPhoneTap(): void {
    this.business.track('phone_tap', { seller_id: this.profile?.id });
  }

  isSponsored(item: any): boolean {
    return item?.sponsored === true;
  }

  businessTag(item: any): string | null {
    return BusinessService.tagFor(item);
  }

  ngOnInit(): void {
    // paramMap rather than a one-shot snapshot: navigating from one seller
    // straight to another reuses this component instance, and a snapshot read
    // would leave the first seller's data on screen.
    this.route.paramMap.subscribe((params) => {
      const id = Number(params.get('id'));
      if (!id) {
        this.notFound = true;
        this.loading = false;
        return;
      }
      this.load(id);
    });
  }

  get userId(): number {
    return this.profile?.id ?? 0;
  }

  /** Store name when there is one, the person's name otherwise. */
  get headline(): string {
    const p = this.profile;
    if (!p) return '';
    return p.store && p.store.name ? p.store.name : p.display_name;
  }

  /**
   * The image to draw in the identity slot: store logo first, then the
   * person's avatar, then nothing (the template falls back to initials).
   *
   * Resolved here rather than in the template so nothing has to dereference
   * a nullable `store` inside an *ngIf — which would break the moment
   * `strictTemplates` is switched on.
   */
  get logoSrc(): string | null {
    const p = this.profile;
    if (!p) return null;
    if (p.store && p.store.logo) return p.store.logo;
    if (!p.store && p.avatar) return p.avatar;
    return null;
  }

  get isStore(): boolean {
    return !!this.profile?.store;
  }

  get storeSubtitle(): string {
    return this.profile?.store?.subtitle ?? '';
  }

  get storeDescription(): string {
    return this.profile?.store?.description ?? '';
  }

  /** SCRUM-98 — city on the store header. */
  get storeCity(): string {
    return this.profile?.store?.city ?? '';
  }

  descriptionExpanded = false;

  /** Long store descriptions collapse; the spec asks for expandable. */
  get descriptionIsLong(): boolean {
    return this.storeDescription.length > 320;
  }

  get shownDescription(): string {
    if (!this.descriptionIsLong || this.descriptionExpanded) {
      return this.storeDescription;
    }
    return this.storeDescription.slice(0, 320).trimEnd() + '…';
  }

  toggleDescription(): void {
    this.descriptionExpanded = !this.descriptionExpanded;
  }

  get memberSinceYear(): number | null {
    const raw = this.profile?.member_since;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d.getFullYear();
  }

  initials(name: string): string {
    const t = (name || '').trim();
    if (!t) return '?';
    const parts = t.split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  /*
   * The card markup below is the SAME design as the main shop/category grid
   * (app-products-container) — same classes, same per-type logic — so a
   * seller's profile page looks identical to the rest of the site instead of
   * the old hand-rolled `.s4-ad` card. These helpers are copied verbatim
   * from ProductsContainerComponent rather than shared via a common base
   * class, to avoid touching that widely-used component.
   */

  addToWishList(event: Event, product: Product): void {
    event.preventDefault();
    this.appService.addToWishList(product);
  }

  /** Icon emoji per resourcetype */
  getCategoryIcon(resourcetype: string | null | undefined): string {
    const map: Record<string, string> = {
      [EntityType.CAR]: '🚗',
      [EntityType.NADLAN]: '🏠',
      [EntityType.SALE2]: '🛍️',
      [EntityType.CELLULAR]: '📱',
      [EntityType.FURNITURE]: '🛋️',
      [EntityType.FASHION]: '👗',
      [EntityType.BOYBABY]: '🧸',
      [EntityType.PET]: '🐾',
      [EntityType.COWORKER]: '💼',
      [EntityType.REQUIRED]: '📋',
    };
    return map[resourcetype ?? ''] ?? '📦';
  }

  /** Human-readable category label */
  getCategoryLabel(item: Product): string {
    const labelMap: Partial<Record<string, string>> = {
      [EntityType.CAR]: item.sconderyCategory?.name ?? 'רכב',
      [EntityType.NADLAN]: item.sconderyCategory?.name ?? 'נדל"ן',
      [EntityType.SALE2]: item.sconderyCategory?.name ?? 'יד שניה',
      [EntityType.CELLULAR]: 'סלולר',
      [EntityType.FURNITURE]: 'ריהוט',
      [EntityType.FASHION]: 'אופנה',
      [EntityType.BOYBABY]: 'תינוקות',
      [EntityType.PET]: 'חיות מחמד',
      [EntityType.COWORKER]: item.professionCategory?.label ?? 'בעלי מקצוע',
      [EntityType.REQUIRED]: item.jobCategory?.label ?? 'דרושים',
    };
    return labelMap[item.resourcetype ?? ''] ?? item.mainCategory?.name ?? '';
  }

  /** Smart title per type — see ProductsContainerComponent.getTitle for the full rationale. */
  getTitle(item: Product): string {
    switch (item.resourcetype) {
      case EntityType.CAR: {
        const producer = typeof item.CodeProducer === 'string'
          ? item.CodeProducer
          : (item.CodeProducer as any)?.producer ?? '';
        const model = typeof item.MainModel === 'string'
          ? item.MainModel
          : (item.MainModel as any)?.model ?? '';
        const year = item.year ?? '';
        return [producer, model, year].filter(Boolean).join(' ') || item.name;
      }
      case EntityType.COWORKER:
        return (!item.isAnonymous && item.businessName)
          ? item.businessName
          : (item.professionalTitle ?? item.name);
      case EntityType.REQUIRED:
        return (!item.isAnonymous && item.companyName)
          ? item.companyName
          : (item.jobTitle ?? item.name);
      case EntityType.PET:
        return item.breed?.label
          ? `${item.breed.label}${item.petName ? ' · ' + item.petName : ''}`
          : item.name;
      default:
        return item.name;
    }
  }

  /** Price label differs by type */
  getPriceLabel(item: Product): string {
    switch (item.resourcetype) {
      case EntityType.COWORKER: return `שכר ${item.pricingType?.label ?? 'שעתי'}`;
      case EntityType.REQUIRED: return `שכר ${item.salaryType?.label ?? 'חודשי'}`;
      case EntityType.PET: return item.freeAdoption ? 'אימוץ' : 'מחיר';
      case EntityType.NADLAN:
        return item.sconderyCategory?.subCategory === 'RENTAL' ? 'שכירות חודשית' : 'מחיר';
      default: return 'מחיר';
    }
  }

  /** Formatted price / range string */
  getDisplayPrice(item: Product): string {
    if (item.freeAdoption) return 'חינם לאימוץ';

    if (item.resourcetype === EntityType.COWORKER) {
      const from = item.priceFrom ? Math.round(+item.priceFrom) : null;
      const to = item.priceTo ? Math.round(+item.priceTo) : null;
      if (from && to) return `₪${from}–₪${to}`;
      if (from) return `מ-₪${from}`;
    }

    if (item.resourcetype === EntityType.REQUIRED) {
      const from = item.salaryFrom ? Math.round(+item.salaryFrom) : null;
      const to = item.salaryTo ? Math.round(+item.salaryTo) : null;
      if (from && to) return `₪${from}–₪${to}`;
      if (from) return `מ-₪${from}`;
    }

    return item.price != null ? `₪${item.price.toLocaleString('he-IL')}` : '—';
  }

  /**
   * True for all Sale2-family types that share the same template block
   * (Sale2, CELLULAR, FURNITURE, FASHION, BOYBABY).
   */
  isSale2Type(resourcetype: string | null | undefined): boolean {
    return [
      EntityType.SALE2,
      EntityType.CELLULAR,
      EntityType.FURNITURE,
      EntityType.FASHION,
      EntityType.BOYBABY,
    ].includes(resourcetype as EntityType);
  }

  trackAd(index: number, item: Product): string | number {
    return item.id ?? index;
  }

  onCategoryChange(): void {
    this.selectedSubcategory = 'all';
    this.rebuildSubcategoryOptions();
    this.applyFilters();
  }

  applyFilters(): void {
    this.filteredAds = this.ads.filter((item) => {
      const categoryMatches = this.selectedCategory === 'all' || this.categoryId(item) === this.selectedCategory;
      const subcategoryMatches = this.selectedSubcategory === 'all' || this.subcategoryId(item) === this.selectedSubcategory;
      return categoryMatches && subcategoryMatches;
    });
  }

  clearFilters(): void {
    this.selectedCategory = 'all';
    this.selectedSubcategory = 'all';
    this.rebuildSubcategoryOptions();
    this.applyFilters();
  }

  private rebuildFilterOptions(): void {
    const options = new Map<string, string>();
    this.ads.forEach((item) => {
      const id = this.categoryId(item);
      if (!id) return;
      options.set(id, this.mainCategory(item)?.name || this.getCategoryLabel(item));
    });
    this.categoryOptions = Array.from(options, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'he'));

    if (this.selectedCategory !== 'all' && !options.has(this.selectedCategory)) {
      this.selectedCategory = 'all';
      this.selectedSubcategory = 'all';
    }
    this.rebuildSubcategoryOptions();
  }

  private rebuildSubcategoryOptions(): void {
    const options = new Map<string, string>();
    this.ads.forEach((item) => {
      if (this.selectedCategory !== 'all' && this.categoryId(item) !== this.selectedCategory) return;
      const id = this.subcategoryId(item);
      const category = this.secondaryCategory(item);
      if (id && category) options.set(id, category.name || category.subCategoryName || 'תת-קטגוריה');
    });
    this.subcategoryOptions = Array.from(options, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'he'));

    if (this.selectedSubcategory !== 'all' && !options.has(this.selectedSubcategory)) {
      this.selectedSubcategory = 'all';
    }
  }

  private categoryId(item: Product): string {
    const ad: any = item;
    const category = this.mainCategory(item);
    const value = category?.id ?? category?.category ?? ad?.resourcetype;
    return value == null ? '' : String(value);
  }

  /**
   * Child ids are only unique inside their parent category in some API tables.
   * Prefixing them with the parent key prevents one category's selection from
   * matching an unrelated child that happens to have the same numeric id.
   */
  private subcategoryId(item: Product): string {
    const category = this.secondaryCategory(item);
    if (!category) return '';
    const value = category.id ?? category.subCategory ?? category.sconderyCategory ?? category.name ?? category.subCategoryName;
    return value == null ? '' : `${this.categoryId(item)}:${String(value)}`;
  }

  private mainCategory(item: Product): any | null {
    const ad: any = item;
    const secondary = this.secondaryCategory(item);
    return ad?.mainCategory
      ?? secondary?.mainCategoryKey
      ?? ad?.subCategory?.sconderyCategory?.mainCategoryKey
      ?? null;
  }

  private secondaryCategory(item: Product): any | null {
    const ad: any = item;
    if (ad?.sconderyCategory && typeof ad.sconderyCategory === 'object') {
      return ad.sconderyCategory;
    }
    if (ad?.subCategory?.sconderyCategory && typeof ad.subCategory.sconderyCategory === 'object') {
      return ad.subCategory.sconderyCategory;
    }
    // Sale2-family responses often expose the useful child id/name directly
    // on subCategory instead of expanding sconderyCategory.
    if (ad?.subCategory && typeof ad.subCategory === 'object') {
      return ad.subCategory;
    }
    return null;
  }

  load(id: number): void {
    this.loading = true;
    this.notFound = false;
    this.failed = false;
    this.page = 1;

    this.sellerService.getProfile(id).subscribe({
      next: (profile) => {
        this.profile = profile;
        this.loading = false;
        this.loadAds(id, 1, true);
      },
      error: (err) => {
        this.loading = false;
        if (err?.status === 404) {
          this.notFound = true;
        } else {
          this.failed = true;
        }
      },
    });
  }

  private loadAds(id: number, page: number, replace: boolean): void {
    if (!replace) this.loadingMore = true;
    this.sellerService.getAds(id, page).subscribe({
      next: (res: Paged<any>) => {
        this.ads = replace ? res.results : this.ads.concat(res.results);
        this.rebuildFilterOptions();
        this.applyFilters();
        this.hasNext = !!res.next;
        this.page = page;
        this.loadingMore = false;
      },
      error: () => {
        // A failed extra page must not destroy what is already on screen.
        this.loadingMore = false;
        this.hasNext = false;
      },
    });
  }

  loadMore(): void {
    if (!this.hasNext || this.loadingMore || !this.profile) return;
    this.loadAds(this.profile.id, this.page + 1, false);
  }

  toggleFollow(): void {
    const p = this.profile;
    if (!p || this.followBusy) return;

    const wasFollowing = p.follow.is_followed_by_me;
    const wasCount = p.follow.followers_count;

    // Optimistic: the button reacts instantly, then reconciles with whatever
    // count the server actually holds.
    this.followBusy = true;
    p.follow.is_followed_by_me = !wasFollowing;
    p.follow.followers_count = wasFollowing ? wasCount - 1 : wasCount + 1;

    const request = wasFollowing
      ? this.sellerService.unfollow(p.id)
      : this.sellerService.follow(p.id);

    request.subscribe({
      next: (res) => {
        this.followBusy = false;
        p.follow.is_followed_by_me = res.following;
        p.follow.followers_count = res.followers;
      },
      error: (err) => {
        this.followBusy = false;
        p.follow.is_followed_by_me = wasFollowing;
        p.follow.followers_count = wasCount;
        if (err?.status === 401 || err?.status === 403) {
          this.followError = 'צריך להתחבר כדי לעקוב';
        } else {
          this.followError = 'הפעולה נכשלה, נסה שוב';
        }
        setTimeout(() => (this.followError = ''), 4000);
      },
    });
  }

  followError = '';

  retry(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) this.load(id);
  }
}
